import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Package, Search, TrendingUp, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Orders = ({ userId }) => {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timelineFilter, setTimelineFilter] = useState('week');
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    if (userId) {
      const loadOrders = async () => {
        try {
          const response = await axios.get(`${API}/orders/user/${userId}`);
          setOrders(response.data);
        } catch (error) {
          console.error('Error fetching orders:', error);
        }
      };
      loadOrders();
    }
  }, [userId]);

  const filteredOrders = useMemo(() => {
    let filtered = orders;

    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.productName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    return filtered;
  }, [orders, searchTerm, statusFilter]);

  const getStatusGradient = (status) => {
    const gradients = {
      'Picked Up': 'from-blue-500 to-blue-500',
      'In Transit': 'from-yellow-500 to-blue-500',
      '1st Attempt': 'from-orange-500 to-red-500',
      '2nd Attempt': 'from-red-500 to-rose-500',
      'Delivered': 'from-green-500 to-emerald-500',
      'RTO': 'from-red-600 to-rose-600',
    };
    return gradients[status] || 'from-gray-500 to-gray-600';
  };

  const statusCounts = {
    'Picked Up': orders.filter(o => o.status === 'Picked Up').length,
    'In Transit': orders.filter(o => o.status === 'In Transit').length,
    '1st Attempt': orders.filter(o => o.status === '1st Attempt').length,
    '2nd Attempt': orders.filter(o => o.status === '2nd Attempt').length,
    'Delivered': orders.filter(o => o.status === 'Delivered').length,
    'RTO': orders.filter(o => o.status === 'RTO').length,
  };

  // Generate timeline data
  const getTimelineData = () => {
    if (orders.length === 0) return [];
    
    switch(timelineFilter) {
      case 'today':
        return Array.from({ length: 24 }, (_, i) => ({
          name: `${i}:00`,
          orders: Math.floor(Math.random() * 3)
        }));
      case 'week':
        return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => ({
          name: day,
          orders: Math.floor(orders.length * (i + 1) / 7)
        }));
      case 'month':
        return Array.from({ length: 4 }, (_, i) => ({
          name: `Week ${i + 1}`,
          orders: Math.floor(orders.length * (i + 1) / 4)
        }));
      default:
        return [];
    }
  };

  const timelineData = getTimelineData();

  return (
    <div className="space-y-6">
      {/* Orders Graph with Timeline Filter */}
      <Card className="bg-white shadow-lg border-slate-200 overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-blue-400" />
              Orders Timeline
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <select value={timelineFilter} onChange={(e) => setTimelineFilter(e.target.value)} className="w-32 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="today">Today</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <div className="h-64 flex items-center justify-center">
              <div className="text-center">
                <Package className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-500">No order data yet</p>
              </div>
            </div>
          ) : (
            <ResponsiveContainer debounce={200} width="100%" height={300}>
              <AreaChart data={timelineData}>
                <defs>
                  <linearGradient id="orderGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Area type="monotone" dataKey="orders" stroke="#3B82F6" strokeWidth={2} fillOpacity={1} fill="url(#orderGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Status Summary Blocks */}
      <div className="grid [&>*]:min-w-0 grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {Object.entries(statusCounts).map(([status, count]) => (
          <Card key={status} className="bg-white shadow-lg border-slate-200 group overflow-hidden relative">
            <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${getStatusGradient(status)} opacity-0 group-hover:opacity-10 transition-opacity`} />
            <CardContent className="pt-6 relative">
              <div className={`text-2xl md:text-3xl font-bold bg-gradient-to-r ${getStatusGradient(status)} bg-clip-text text-transparent`}>{count}</div>
              <div className="text-xs text-slate-500 mt-1">{status}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Filters */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search by Order ID or Product Name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white border-slate-200 focus:border-blue-500"
                />
              </div>
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full md:w-48 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">All Status</option>
              <option value="Picked Up">Picked Up</option>
              <option value="In Transit">In Transit</option>
              <option value="1st Attempt">1st Attempt</option>
              <option value="2nd Attempt">2nd Attempt</option>
              <option value="Delivered">Delivered</option>
              <option value="RTO">RTO</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <Card className="bg-white shadow-lg border-slate-200">
            <CardContent className="py-12 text-center">
              <Package className="w-16 h-16 mx-auto mb-4 text-slate-600 animate-pulse" />
              <p className="text-slate-500 text-lg mb-2">No orders found</p>
              <p className="text-slate-400 text-sm">Orders will appear here once added by admin</p>
            </CardContent>
          </Card>
        ) : (
          filteredOrders.map((order) => (
            <Card key={order._id} className="bg-white shadow-lg border-slate-200 hover:border-white/30 transition-all hover:shadow-lg group overflow-hidden relative">
              <div className={`pointer-events-none absolute inset-0 bg-gradient-to-r ${getStatusGradient(order.status)} opacity-0 group-hover:opacity-5 transition-opacity`} />
              <CardContent className="pt-6 relative">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-lg bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">{order.orderId}</h4>
                    <p className="text-sm text-slate-500 mt-1">{order.productName}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-2xl text-blue-600">₹{order.price}</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getStatusGradient(order.status)} text-slate-800 mt-1`}>
                      {order.status}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                  className="w-full hover:bg-white flex items-center justify-center space-x-2"
                >
                  <span>{expandedOrder === order._id ? 'Hide Details' : 'Show Details'}</span>
                  {expandedOrder === order._id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
                {expandedOrder === order._id && (
                  <div className="mt-4 pt-4 border-t border-slate-200 space-y-3 text-sm animate-in slide-in-from-top">
                    <div className="flex justify-between items-center p-2 bg-white rounded">
                      <span className="text-slate-500">Tracking ID:</span>
                      <span className="font-mono text-blue-400">{order.trackingId}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-white rounded">
                      <span className="text-slate-500">Store Name:</span>
                      <span className="text-slate-800">{order.storeName || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-white rounded">
                      <span className="text-slate-500">Dispatch Date:</span>
                      <span className="text-slate-800">{new Date(order.dispatchDate).toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Orders;